import React, { useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { CloudUpload, DeleteOutline, Download, PictureAsPdf, Visibility } from '@mui/icons-material';
import { FORM_REFS } from '../../constants/certificationAuthorization';
import GetsBrandHeader from './GetsBrandHeader';

const RESULT_OPTIONS = [
  { value: 'SAT', label: 'SAT' },
  { value: 'UNSAT', label: 'UNSAT' },
  { value: 'NA', label: 'N/A' }
];

/**
 * GETS/CM/003 — Competency Assessment Form for Certifying / Support Staff
 */
function GetsCm003CompetencyForm({
  application,
  value,
  onChange,
  readOnly = false,
  onSave,
  onMarkComplete,
  onDownloadPdf,
  onUploadAttachment,
  onViewAttachment,
  onDownloadAttachment,
  onDeleteAttachment,
  saving = false
}) {
  const fileInputRef = useRef(null);
  const ca = value || {};
  const rows = Array.isArray(ca.rows) ? ca.rows : [];
  const attachments = Array.isArray(ca.attachments) ? ca.attachments : [];

  const set = (patch) => onChange({ ...ca, ...patch });
  const updateRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ rows: next });
  };

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderColor: 'text.primary',
        borderWidth: 2,
        borderStyle: 'double'
      }}
    >
      <GetsBrandHeader
        title="Competency Assessment Form for Certifying / Support Staff"
        subtitle={`Form: ${FORM_REFS.competency} | Issue: 01 Rev 00 Dtd Jul 2026`}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {onDownloadPdf && (
          <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
            Download {FORM_REFS.competency} PDF
          </Button>
        )}
        {!readOnly && onUploadAttachment && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUploadAttachment(file);
                e.target.value = '';
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUpload />}
              disabled={saving}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload document
            </Button>
          </>
        )}
        {!readOnly && onSave && (
          <Button size="small" variant="contained" disabled={saving} onClick={onSave}>
            Save assessment
          </Button>
        )}
        {!readOnly && onMarkComplete && (
          <Button size="small" variant="contained" color="success" disabled={saving} onClick={onMarkComplete}>
            Mark complete
          </Button>
        )}
      </Stack>

      {/* Section A */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase' }}>
          Section A: (For Applicant)
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Name in Full"
              value={application?.applicantName || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Employee / Staff No."
              value={application?.employeeId || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Designation"
              value={ca.designation || application?.designation || ''}
              onChange={(e) => set({ designation: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Application"
              InputLabelProps={{ shrink: true }}
              value={
                ca.dateOfApplication
                  ? String(ca.dateOfApplication).slice(0, 10)
                  : application?.createdAt
                    ? String(application.createdAt).slice(0, 10)
                    : ''
              }
              onChange={(e) => set({ dateOfApplication: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label="Purpose"
              value={ca.purpose || ''}
              onChange={(e) => set({ purpose: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Section B */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, textTransform: 'uppercase' }}>
          Section B: Vetting of Application (For Assessors)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Strike which is not applicable — SAT / UNSAT / N/A
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40}>Sl.</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width={120}>Result</TableCell>
              <TableCell width={160}>Remarks</TableCell>
              <TableCell width={140}>Assessor Signature</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.slNo || index}>
                <TableCell>{row.slNo}</TableCell>
                <TableCell>
                  <Typography variant="body2">{row.description}</Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={row.result || ''}
                    disabled={readOnly}
                    onChange={(e) => updateRow(index, { result: e.target.value || undefined })}
                  >
                    <MenuItem value="">—</MenuItem>
                    {RESULT_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.remarks || ''}
                    onChange={(e) => updateRow(index, { remarks: e.target.value })}
                    {...fieldProps}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.assessorSignature || ''}
                    onChange={(e) => updateRow(index, { assessorSignature: e.target.value })}
                    {...fieldProps}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Section C */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase' }}>
          Section C: Assessment Board Details (Overall Suitability)
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={ca.assessmentDate ? String(ca.assessmentDate).slice(0, 10) : ''}
              onChange={(e) => set({ assessmentDate: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Venue"
              value={ca.venue || ''}
              onChange={(e) => set({ venue: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl disabled={readOnly}>
              <FormLabel sx={{ typography: 'caption' }}>Overall suitability</FormLabel>
              <RadioGroup
                row
                value={ca.overallSuitability || ''}
                onChange={(e) => set({ overallSuitability: e.target.value })}
              >
                <FormControlLabel value="ACCEPTABLE" control={<Radio size="small" />} label="Acceptable" />
                <FormControlLabel value="NOT_ACCEPTABLE" control={<Radio size="small" />} label="Not acceptable" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Conclusion / Remarks"
              value={ca.conclusionRemarks || ''}
              onChange={(e) => set({ conclusionRemarks: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Board result (SATISFACTORY / UNSATISFACTORY)"
              value={ca.boardResult || ''}
              onChange={(e) => set({ boardResult: e.target.value || undefined })}
              disabled={readOnly}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="SATISFACTORY">SATISFACTORY</MenuItem>
              <MenuItem value="UNSATISFACTORY">UNSATISFACTORY</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Signature & Stamp of CMM"
              value={ca.cmmSignature || ''}
              onChange={(e) => set({ cmmSignature: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Compliance Monitoring Manager Remarks"
              value={ca.mroQmRemarks || ''}
              onChange={(e) => set({ mroQmRemarks: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Type rated AME Sig. & Stamp / AME Lic. No."
              value={ca.ameBoardSignature || ''}
              onChange={(e) => set({ ameBoardSignature: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="AME Lic. No."
              value={ca.ameLicenseNo || ''}
              onChange={(e) => set({ ameLicenseNo: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="LMM / BMM"
              value={ca.lmmBmmBoardSignature || ''}
              onChange={(e) => set({ lmmBmmBoardSignature: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
          The Applicant has been reviewed and assessed and meets the company standards (when overall is Acceptable).
        </Alert>
      </Box>

      {/* Attachments */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Supporting documents
        </Typography>
        {attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents uploaded.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {attachments.map((att) => (
              <Stack
                key={att._id || att.fileUrl}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                  {att.fileName || 'Document'}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {att.fileUrl && onViewAttachment && (
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => onViewAttachment(att)}
                    >
                      View
                    </Button>
                  )}
                  {att.fileUrl && onDownloadAttachment && (
                    <Button
                      size="small"
                      startIcon={<Download />}
                      onClick={() => onDownloadAttachment(att)}
                    >
                      Download
                    </Button>
                  )}
                  {!readOnly && onDeleteAttachment && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutline />}
                      onClick={() => onDeleteAttachment(att)}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

export default GetsCm003CompetencyForm;
